import React, { useMemo } from 'react';
import { 
  Home, 
  Building2, 
  Store, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Check,
  Send,
  ArrowRight
} from 'lucide-react';
import { useSimulateur } from '../SimulateurContext';
import { Button } from '../../ui/button';
import { diagnosticPricing, calculateDiagnosticPrice } from '../../../utils/pricing';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Step6Summary = () => {
  const { 
    formData, 
    goToPrevStep, 
    goToStep,
    isSubmitting, 
    setIsSubmitting,
    setIsSubmitted,
    isSubmitted
  } = useSimulateur();

  const propertyTypeLabels = {
    appartement: { label: 'Appartement', icon: Building2 },
    maison: { label: 'Maison', icon: Home },
    local: { label: 'Local commercial', icon: Store },
  };

  const transactionLabels = {
    vente: 'Vente',
    location: 'Location',
  };

  // Calculate prices
  const quoteDetails = useMemo(() => {
    const selectedDiagnostics = formData.selectedDiagnostics || [];
    const details = selectedDiagnostics.map(key => {
      const diagnostic = diagnosticPricing[key];
      const price = calculateDiagnosticPrice(
        key,
        parseFloat(formData.surface) || 50,
        formData.propertyType,
        parseInt(formData.constructionYear)
      );
      return {
        key,
        name: diagnostic?.name || key,
        price
      };
    });

    const subtotal = details.reduce((sum, d) => sum + d.price, 0);
    const count = details.length;
    
    let discountRate = 0;
    if (count >= 4) discountRate = 15;
    else if (count >= 2) discountRate = 10;
    
    const discount = Math.round(subtotal * (discountRate / 100));
    const total = subtotal - discount;

    return { details, subtotal, discount, discountRate, total, count };
  }, [formData]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/devis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientName: formData.clientName,
          email: formData.email,
          phone: formData.phone,
          propertyType: formData.propertyType,
          surface: parseFloat(formData.surface),
          constructionYear: parseInt(formData.constructionYear),
          city: formData.city,
          postalCode: formData.postalCode,
          interventionDate: formData.preferredDate,
          selectedDiagnostics: formData.selectedDiagnostics,
          quote: quoteDetails,
          // Additional fields
          transactionType: formData.transactionType,
          address: formData.address,
          hasGas: formData.hasGas,
          hasCellar: formData.hasCellar,
          message: formData.message,
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }

      setIsSubmitted(true);
      toast.success("Demande envoyée !", {
        description: "Vous recevrez votre devis par email sous quelques minutes."
      });

    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success screen
  if (isSubmitted) {
    return (
      <div className="text-center space-y-8 py-12">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-10 h-10 text-emerald-600" />
        </div>
        
        <div className="space-y-3">
          <h2 className="text-3xl font-bold text-stone-900">
            Demande envoyée !
          </h2>
          <p className="text-lg text-stone-600 max-w-md mx-auto">
            Merci {formData.clientName} ! Vous allez recevoir votre devis détaillé par email à l'adresse <strong>{formData.email}</strong>
          </p>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 max-w-md mx-auto">
          <h3 className="font-semibold text-emerald-800 mb-2">Prochaines étapes :</h3>
          <ul className="text-left text-emerald-700 space-y-2">
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
              <span>Consultez votre boîte email</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
              <span>Anaïs vous contactera sous 24h pour confirmer le rendez-vous</span>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
              <span>Vous pouvez aussi la contacter directement : <strong>06 52 54 79 19</strong></span>
            </li>
          </ul>
        </div>

        <Button
          onClick={() => window.location.href = '/'}
          className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="btn-back-home"
        >
          Retour à l'accueil
        </Button>
      </div>
    );
  }

  const PropertyIcon = propertyTypeLabels[formData.propertyType]?.icon || Home;

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-3xl font-bold text-stone-900">
          Récapitulatif de votre demande
        </h2>
        <p className="text-stone-600">
          Vérifiez les informations avant de valider
        </p>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Info */}
        <div className="bg-white border-2 border-stone-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <PropertyIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900">Le bien</h3>
            <button 
              onClick={() => goToStep(2)}
              className="ml-auto text-sm text-emerald-600 hover:underline"
              data-testid="edit-property"
            >
              Modifier
            </button>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-500">Type</span>
              <span className="font-medium text-stone-900">
                {propertyTypeLabels[formData.propertyType]?.label} en {transactionLabels[formData.transactionType]}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Surface</span>
              <span className="font-medium text-stone-900">{formData.surface} m²</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Construction</span>
              <span className="font-medium text-stone-900">
                {formData.constructionYear >= 2007 ? 'Après 2007' :
                 formData.constructionYear >= 1997 ? '1997-2007' :
                 formData.constructionYear >= 1949 ? '1949-1997' : 'Avant 1949'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Installation gaz</span>
              <span className="font-medium text-stone-900">{formData.hasGas ? 'Oui' : 'Non'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Adresse</span>
              <span className="font-medium text-stone-900 text-right">
                {formData.address}<br/>
                {formData.postalCode} {formData.city}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white border-2 border-stone-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900">Contact</h3>
            <button 
              onClick={() => goToStep(5)}
              className="ml-auto text-sm text-emerald-600 hover:underline"
              data-testid="edit-contact"
            >
              Modifier
            </button>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-stone-400" />
              <span className="font-medium text-stone-900">{formData.clientName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-stone-400" />
              <span className="font-medium text-stone-900">{formData.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-stone-400" />
              <span className="font-medium text-stone-900">{formData.phone}</span>
            </div>
            {formData.preferredDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-stone-400" />
                <span className="font-medium text-stone-900">
                  {new Date(formData.preferredDate).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Diagnostics & Price */}
        <div className="lg:col-span-2 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Diagnostics sélectionnés</h3>
            <button 
              onClick={() => goToStep(4)}
              className="text-sm text-emerald-200 hover:text-white hover:underline"
              data-testid="edit-diagnostics"
            >
              Modifier
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mb-6">
            {quoteDetails.details.map((item) => (
              <div key={item.key} className="flex justify-between text-sm py-1">
                <span className="text-emerald-100">{item.name}</span>
                <span className="font-medium">{item.price}€</span>
              </div>
            ))}
          </div>
          
          <div className="border-t border-emerald-500 pt-4 space-y-2">
            <div className="flex justify-between text-emerald-100">
              <span>Sous-total ({quoteDetails.count} diagnostics)</span>
              <span>{quoteDetails.subtotal}€</span>
            </div>
            {quoteDetails.discount > 0 && (
              <div className="flex justify-between text-emerald-200">
                <span>Réduction pack ({quoteDetails.discountRate}%)</span>
                <span>-{quoteDetails.discount}€</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-2">
              <span>Total TTC</span>
              <span>{quoteDetails.total}€</span>
            </div>
          </div>
        </div>
      </div>

      {/* Legal text */}
      <div className="max-w-4xl mx-auto text-center">
        <p className="text-xs text-stone-500">
          En validant cette demande, vous acceptez d'être recontacté par Mon Diag Immo pour le suivi de votre demande. 
          Vos données sont traitées conformément à notre{' '}
          <a href="/cgv" className="text-emerald-600 hover:underline">politique de confidentialité</a>.
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 max-w-4xl mx-auto">
        <Button
          type="button"
          variant="outline"
          onClick={goToPrevStep}
          className="px-6 py-3 rounded-xl"
          data-testid="btn-prev-step6"
        >
          Retour
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-8 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          data-testid="btn-submit-quote"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Envoyer ma demande
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
